import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { Loader2, AlertCircle, Info, Linkedin } from "lucide-react";
import { useContentManager } from "@/hooks/useContentManager";
import { useAuth } from "@/contexts/AuthContext";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { getPersistenceType, setPersistenceType, isPrivateBrowsingMode } from "@/utils/auth/sessionManager";

type AuthMode = "signin" | "signup";

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [linkedInLoading, setLinkedInLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<string | null>(null);
  const [mode, setMode] = useState<AuthMode>("signin");
  const [showHelp, setShowHelp] = useState(false);
  const [rememberMe, setRememberMe] = useState<boolean>(getPersistenceType() === 'local');
  const [isPrivateMode, setIsPrivateMode] = useState<boolean>(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { parseDeepLinkState } = useContentManager();
  const { signIn, signUp, signInWithLinkedIn, isAuthenticated } = useAuth();
  
  // Extract redirect information from location state or query parameters
  const [redirectPath, setRedirectPath] = useState<string>("/dashboard");
  const [redirectState, setRedirectState] = useState<Record<string, any>>({});
  
  useEffect(() => {
    // Check for private browsing mode on mount
    setIsPrivateMode(isPrivateBrowsingMode());
    
    // Clear any previous errors when mode changes
    setError(null);
    setErrorType(null);
    
    // First check location state
    if (location.state?.from) {
      setRedirectPath(location.state.from);
      
      // Remove the 'from' property from redirectState
      const { from, ...restState } = location.state;
      setRedirectState(restState || {});
    } 
    // Then check URL params
    else if (location.search) {
      const params = new URLSearchParams(location.search);
      const fromPath = params.get('from');
      
      if (fromPath) {
        setRedirectPath(fromPath);
        
        // Parse other query params as state
        const queryState = parseDeepLinkState(location.search);
        // Remove the 'from' property from state
        const { from, ...restState } = queryState;
        setRedirectState(restState || {});
      }
    }
  }, [location, parseDeepLinkState, mode]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectPath, { state: redirectState });
    }
  }, [isAuthenticated, navigate, redirectPath, redirectState]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setErrorType(null);

    // Set persistence type based on remember me checkbox
    setPersistenceType(rememberMe ? 'local' : 'session');

    try {
      if (mode === "signin") {
        const result = await signIn(email, password);
        
        if (result?.error) {
          setError(result.error.message || "Failed to sign in");
          setErrorType(result.errorType || null);
        }
      } else {
        const result = await signUp(email, password);
        
        if (result?.error) {
          setError(result.error.message || "Failed to sign up");
          setErrorType(result.errorType || null);
        }
      }
    } catch (error: any) {
      console.error("Authentication error:", error);
      setError(error.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleLinkedInSignIn = async () => {
    setLinkedInLoading(true);
    setError(null);
    setErrorType(null);

    try {
      const result = await signInWithLinkedIn();
      
      if (result?.error) {
        setError(result.error.message || "Failed to sign in with LinkedIn");
        setErrorType(result.errorType || null);
      }
      // No need to navigate - OAuth will redirect the user
    } catch (error: any) {
      console.error("LinkedIn authentication error:", error);
      setError(error.message || "An unexpected error occurred");
    } finally {
      setLinkedInLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === "signin" ? "signup" : "signin");
    setError(null);
    setErrorType(null);
  };

  const handleRememberMeChange = (checked: boolean) => {
    setRememberMe(checked);
  };

  const renderErrorGuidance = () => {
    if (!errorType) return null;
    
    switch (errorType) {
      case 'invalid_credentials':
        return (
          <div className="mt-2 text-sm text-slate-600">
            <ul className="list-disc pl-5 space-y-1">
              <li>Double-check your email address for typos</li>
              <li>Make sure your password is correct</li>
              <li>Passwords are case-sensitive</li>
            </ul>
          </div>
        );
      case 'user_not_found':
        return (
          <div className="mt-2 text-sm text-slate-600">
            <p>This email address is not registered in our system.</p>
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm text-slate-800 font-semibold" 
              onClick={toggleMode}
            >
              Create a new account instead
            </Button>
          </div>
        );
      case 'email_taken':
        return (
          <div className="mt-2 text-sm text-slate-600">
            <p>An account with this email already exists.</p>
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm text-slate-800 font-semibold" 
              onClick={() => setMode("signin")}
            >
              Try signing in instead
            </Button>
          </div>
        );
      case 'weak_password':
        return (
          <div className="mt-2 text-sm text-slate-600">
            <p>For better security, your password should:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Be at least 8 characters long</li>
              <li>Include uppercase and lowercase letters</li>
              <li>Include numbers and special characters</li>
            </ul>
          </div>
        );
      case 'network_error':
        return (
          <div className="mt-2 text-sm text-slate-600">
            <p>Unable to connect to our authentication service:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Check your internet connection</li>
              <li>Try using a different network if available</li>
              <li>The service may be temporarily unavailable</li>
            </ul>
            <Button 
              variant="outline" 
              className="mt-2 text-sm h-8" 
              onClick={handleAuth}
              disabled={loading}
            >
              Try again
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md px-4"
      >
        <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-slate-200/60">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="text-3xl font-bold text-center mb-2 bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
              {mode === "signin" ? "Welcome Back" : "Create Account"}
            </h1>
            <p className="text-slate-600 text-center mb-6">
              {mode === "signin" 
                ? "Sign in to access your account" 
                : "Sign up to get started with our platform"}
            </p>
            {/* Show redirect information if coming from another page */}
            {redirectPath !== "/dashboard" && (
              <p className="text-slate-500 text-center text-sm mb-4">
                You'll be redirected to your previously viewed page after signing in.
              </p>
            )}
          </motion.div>
          
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4"
            >
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle className="font-medium">Authentication Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
                {renderErrorGuidance()}
              </Alert>
            </motion.div>
          )}
          
          {isPrivateMode && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4"
            >
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle className="font-medium">Private Browsing Detected</AlertTitle>
                <AlertDescription>
                  You appear to be in private/incognito mode. Your session will end when you close the browser.
                </AlertDescription>
              </Alert>
            </motion.div>
          )}
          
          {/* LinkedIn sign-in button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-6"
          >
            <Button
              type="button"
              className="w-full flex items-center justify-center gap-2 bg-[#0077B5] hover:bg-[#006699] transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
              onClick={handleLinkedInSignIn}
              disabled={linkedInLoading}
            >
              {linkedInLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Connecting...</span>
                </div>
              ) : (
                <>
                  <Linkedin className="h-5 w-5" />
                  <span>Sign in with LinkedIn</span>
                </>
              )}
            </Button>
          </motion.div>

          <div className="relative flex items-center justify-center mb-6">
            <div className="border-t border-slate-200 flex-grow"></div>
            <div className="mx-4 text-sm text-slate-500">or</div>
            <div className="border-t border-slate-200 flex-grow"></div>
          </div>
          
          <form onSubmit={handleAuth} className="space-y-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full transition-all duration-200 focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
                disabled={loading}
                required
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                Password
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full transition-all duration-200 focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
                disabled={loading}
                required
                minLength={6}
              />
              {mode === "signup" && (
                <p className="text-xs text-slate-500 mt-1">
                  Password must be at least 6 characters long.
                </p>
              )}
            </motion.div>
            
            {/* Remember Me Checkbox */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.55 }}
              className="flex items-center space-x-2"
            >
              <Checkbox 
                id="remember-me" 
                checked={rememberMe} 
                onCheckedChange={handleRememberMeChange}
                disabled={isPrivateMode}
              />
              <label 
                htmlFor="remember-me" 
                className={`text-sm text-slate-700 ${isPrivateMode ? 'opacity-50' : ''}`}
              >
                Remember me
              </label>
              {isPrivateMode && (
                <span className="text-xs text-slate-500">(unavailable in private mode)</span>
              )}
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-700 hover:to-slate-600 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Processing...</span>
                  </div>
                ) : mode === "signin" ? (
                  "Sign In"
                ) : (
                  "Sign Up"
                )}
              </Button>
            </motion.div>
          </form>
          
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-6 text-center"
          >
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={toggleMode}
                className="text-sm text-slate-600 hover:text-slate-900 transition-colors duration-200"
                disabled={loading}
              >
                {mode === "signin" 
                  ? "Don't have an account? Sign up" 
                  : "Already have an account? Sign in"}
              </button>
              
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowHelp(true)}
                className="text-sm text-slate-600"
                disabled={loading}
              >
                <Info className="h-4 w-4 mr-1" />
                Help
              </Button>
            </div>
          </motion.div>
        </div>
      </motion.div>
      
      {/* Help Dialog */}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Authentication Help</DialogTitle>
            <DialogDescription>
              Common questions and solutions for sign in issues
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div>
              <h3 className="font-medium text-slate-900">I forgot my password</h3>
              <p className="text-sm text-slate-600 mt-1">
                Currently, you'll need to contact the administrator to reset your password.
                Password reset functionality will be available soon.
              </p>
            </div>
            
            <div>
              <h3 className="font-medium text-slate-900">I'm not receiving verification emails</h3>
              <p className="text-sm text-slate-600 mt-1">
                Check your spam folder first. If you still don't see it, try signing up again
                or contact support for assistance.
              </p>
            </div>
            
            <div>
              <h3 className="font-medium text-slate-900">I can't access my account</h3>
              <p className="text-sm text-slate-600 mt-1">
                Make sure you're using the correct email address. If you've previously signed up
                with a different method (e.g., Google, GitHub), try using that instead.
              </p>
            </div>
            
            <div>
              <h3 className="font-medium text-slate-900">About session persistence</h3>
              <p className="text-sm text-slate-600 mt-1">
                Enabling "Remember me" keeps you signed in even after closing the browser.
                Without it, you'll need to sign in again when you close your browser.
                {isPrivateMode && " This option is unavailable in private/incognito mode."}
              </p>
            </div>
            
            <div>
              <h3 className="font-medium text-slate-900">I'm having other technical issues</h3>
              <p className="text-sm text-slate-600 mt-1">
                Please contact support with details about the problem you're experiencing.
                Include any error messages you see for faster assistance.
              </p>
            </div>
            
            <div>
              <h3 className="font-medium text-slate-900">LinkedIn Authentication</h3>
              <p className="text-sm text-slate-600 mt-1">
                When signing in with LinkedIn, you'll be redirected to LinkedIn's authentication page.
                After authorizing access, you'll be returned to our application and logged in automatically.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => setShowHelp(false)}
              className="w-full sm:w-auto"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Auth;
